import Phaser from '../lib/phaser.js';
import { SCENE_KEYS } from './scene-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { BaseScene } from './base-scene.js';
import { dataManager } from '../utils/data-manager.js';
import { netManager } from '../net/index.js';
import {
  apiConfigured,
  fetchShopCards,
  fetchShopOrders,
  placeShopOrder,
  placeGachaOrder,
  fetchPlayer,
  fetchTokenConfig,
  fetchTokenBalance,
  fetchTopupConfig,
  buyGoldPack,
  buyGoldBooster,
  fetchVaultStakes,
  stakeVaultCard,
  claimVaultGold,
  requestVaultUnstake,
} from '../net/api.js';
import { canPayToken, connectPayProvider, payTokenToTreasury, paySolToTreasury } from '../net/token-pay.js';
import { GACHA_TIERS, gachaGoldPrice } from '@mn/shared';

// ── Card Shop — the headline gameplay reward. ──
// Premium, FULLY MOUSE-DRIVEN scene where players redeem their hard-earned Gold for REAL graded
// Pokémon cards (GET /shop/cards), place a shipping order (POST /shop/order), and track their
// redemptions (GET /shop/orders). Matches the Monstara look: dark glass panels, emerald/gold,
// corner brackets. Card images are remote URLs, so they render as DOM <img> overlays (positioned
// over Phaser card frames) — robust against cross-origin tainting and gives free fallback styling.
// Everything degrades gracefully offline: no api/wallet → a calm "connect to shop" state, no crash.

const C = Object.freeze({
  emerald: '#34d399',
  emeraldLight: '#aef0c4',
  gold: '#f5b23e',
  text: '#eafff2',
  sub: '#9fc4ad',
  danger: '#ff7a7a',
  panel: 0x0e1712,
  panelStrokeNum: 0x2c4a38,
  emeraldNum: 0x34d399,
  goldNum: 0xf5b23e,
});

/**
 * @typedef CardShopSceneData
 * @type {object}
 * @property {string} [previousSceneName]
 */

export class CardShopScene extends BaseScene {
  /** @type {CardShopSceneData} */ #sceneData;
  /** @type {import('../net/api.js').ShopCard[]} */ #cards;
  /** @type {number} */ #gold;
  /** @type {number} */ #maxUsd;
  /** @type {'grid' | 'orders' | 'gacha' | 'topup' | 'vault'} */ #view;
  /** @type {import('../net/api.js').TopupConfig | null} cached top-up config (live $TOKEN/SOL quotes) */ #topupCfg = null;
  /** @type {number} when #topupCfg was fetched (quotes are valid ~quoteTtlMs) */ #topupAt = 0;
  /** @type {boolean} */ #loading;
  /** @type {number} $MONSTARA holder discount % applied server-side (0 = none). */ #holderDiscountPct;
  /** @type {Phaser.GameObjects.Container | undefined} header discount badge */ #discountBadge;
  /** @type {Phaser.GameObjects.Container | undefined} */ #gridContainer;
  /** @type {Phaser.GameObjects.Text | undefined} */ #statusText;
  /** @type {Phaser.GameObjects.Text | undefined} */ #goldText;
  /** @type {boolean} guard against re-entrant / double close (rapid double-tap, shutdown race) */ #closing = false;
  /** @type {HTMLElement[]} */ #domEls;
  /** @type {HTMLDivElement | undefined} */ #scrollEl;
  /** @type {number} */ #panelX;
  /** @type {number} */ #panelY;
  /** @type {number} */ #panelW;
  /** @type {number} */ #panelH;
  /** @type {boolean} narrow/phone viewport → condensed header + fewer grid columns */ #narrow = false;
  /** @type {import('@mn/shared').TokenConfigResponse | null} $TOKEN feature config (null until probed) */ #tokenConfig = null;
  /** @type {number} live $TOKEN balance (for the token pay option) */ #tokenBalance = 0;
  /** @type {string} active CC category tab */ #category = 'Pokemon';
  /** @type {string} active name-search query */ #search = '';
  /** @type {number} current page (0-based) */ #page = 0;
  /** @type {number} total pages for the current category/search */ #totalPages = 1;
  /** @type {HTMLDivElement | undefined} persistent toolbar (categories + search + pager) */ #toolbarEl;
  /** @type {HTMLButtonElement | undefined} always-on-top DOM close ✕ (universal escape hatch) */ #closeEl;
  /** @type {HTMLSpanElement | undefined} pager "page X/Y" label */ #pagerLabel;
  /** @type {HTMLInputElement | undefined} search box */ #searchEl;
  /** @type {{cat:string, el:HTMLButtonElement}[]} category buttons (for active styling) */ #catBtns = [];

  constructor() {
    super({ key: SCENE_KEYS.CARD_SHOP_SCENE });
  }

  /** @param {CardShopSceneData} data */
  init(data) {
    super.init(data);
    this.#sceneData = data || {};
    this.#cards = [];
    this.#gold = dataManager.getGold();
    this.#maxUsd = 0; // 0 = no filter
    this.#view = 'grid';
    this.#loading = true;
    this.#holderDiscountPct = 0;
    this.#domEls = [];
    this.#category = 'Pokemon';
    this.#search = '';
    this.#page = 0;
    this.#totalPages = 1;
    this.#catBtns = [];
    this.#tokenConfig = null;
    this.#tokenBalance = 0;
  }

  create() {
    super.create();
    this._claimOverlayInput();
    const w = this.scale.width;
    const h = this.scale.height;

    // Narrow phones: condense the header (smaller title, hide the long subtitle, narrower gold chip)
    // so nothing overflows a ~390px portrait viewport. `#narrow` also drives the grid column count.
    this.#narrow = Math.min(w, h) <= 560;
    const narrow = this.#narrow;

    // dark scrim
    this.add.rectangle(0, 0, w, h, 0x05100a, 0.9).setOrigin(0);

    // Clean, non-overlapping header laid out in three rows above the panel:
    //   row 1 → GOLD chip (left) · "CARD SHOP" title (center)
    //   row 2 → SHOP / MY ORDERS tabs (left) · ✕ CLOSE (right)
    //   row 3 → subtitle (left) · price-filter chips (right)
    this.#panelW = Math.min(1100, w - (narrow ? 24 : 80));
    const left = w / 2 - this.#panelW / 2;
    const right = w / 2 + this.#panelW / 2;

    const ROW1 = 36; // title + gold chip
    const ROW2 = 86; // tabs + close
    const ROW3 = 130; // subtitle + filters
    const HEADER_BOTTOM = 154;

    // headline (centered) — smaller on narrow screens so it never collides with the gold chip
    this.add
      .text(w / 2, ROW1, 'CARD SHOP', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: narrow ? '26px' : '40px',
        color: C.emerald,
        stroke: '#05351f',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setShadow(0, 0, '#34d399', 18, true, true);

    // gold balance chip (top-left, aligned to panel's left edge) — narrower on phones
    const chipX = left + 8;
    const chipW = narrow ? 132 : 200;
    this.add.rectangle(chipX, ROW1, chipW, 42, C.panel, 0.95).setOrigin(0, 0.5).setStrokeStyle(2, C.goldNum, 0.8);
    this.add
      .text(chipX + 14, ROW1, 'GOLD', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: narrow ? '15px' : '18px', color: C.gold })
      .setOrigin(0, 0.5);
    this.#goldText = this.add
      .text(chipX + chipW - 16, ROW1, `${this.#gold}`, { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: narrow ? '18px' : '22px', color: C.gold })
      .setOrigin(1, 0.5);

    // subtitle (row 3, left-aligned) — hidden on narrow screens to free up the filter row
    if (!narrow) {
      this.add
        .text(left + 8, ROW3, 'Redeem your Gold for REAL graded Pokémon cards.', {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '16px',
          color: C.emeraldLight,
        })
        .setOrigin(0, 0.5);
    }

    // main glass panel (sits below the full header)
    this.#panelH = h - HEADER_BOTTOM - 20;
    this.#panelX = w / 2;
    this.#panelY = HEADER_BOTTOM + this.#panelH / 2;
    this.add.rectangle(this.#panelX, this.#panelY, this.#panelW, this.#panelH, C.panel, 0.94).setStrokeStyle(2, C.emeraldNum, 0.5);
    this.#cornerBrackets(this.#panelX, this.#panelY, this.#panelW, this.#panelH);

    // top bar: tabs + filter + close (rows 2 + 3)
    this.#buildTopBar(left, right, ROW2, ROW3);

    // status / loading text (centered in panel)
    this.#statusText = this.add
      .text(this.#panelX, this.#panelY, 'Loading cards…', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '22px',
        color: C.sub,
      })
      .setOrigin(0.5);

    this.#gridContainer = this.add.container(0, 0);
    this.#buildToolbar();

    this.cameras.main.fadeIn(300, 0, 0, 0);

    // UNIVERSAL escape hatches so the shop can ALWAYS be closed — players reported a state where the
    // close button was unreachable (a DOM tab overlay over it) and they had to RESTART the game.
    // (1) ESC key, and (2) a fixed DOM ✕ pinned above every overlay. Kept OUT of #domEls so a tab
    // switch's #destroyDom never removes it; torn down on shutdown.
    this.input.keyboard?.on('keydown-ESC', () => this.#close());
    const x = document.createElement('button');
    x.textContent = '✕';
    x.setAttribute('aria-label', 'Close shop');
    x.style.cssText =
      'position:fixed;top:14px;right:14px;width:44px;height:44px;border-radius:50%;border:1px solid rgba(255,255,255,0.35);background:rgba(20,16,40,0.92);color:#fff;font:700 20px system-ui;cursor:pointer;z-index:200';
    x.onclick = () => this.#close();
    document.body.appendChild(x);
    this.#closeEl = x;

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.#destroyDom();
      this.#toolbarEl?.remove();
      this.#toolbarEl = undefined;
      this.#closeEl?.remove();
      this.#closeEl = undefined;
    });

    void this.#refreshGold();
    void this.#loadTokenConfig();
    // Open directly on a requested tab (e.g. the VAULT menu button opens the Card Vault), else the
    // shop grid. #switchView (no-op when already on `view`) handles the tab styling + render + toolbar.
    const requestedView = this.#sceneData?.view;
    if (['gacha', 'topup', 'vault', 'orders'].includes(requestedView)) {
      this.#switchView(requestedView);
    } else {
      void this.#renderView('grid');
    }
  }

  /**
   * Probe the $TOKEN feature so the confirm modal can offer a "Pay with $TOKEN" option. Fails
   * closed: any error leaves the feature disabled (Gold-only), never blocks the shop.
   */
  async #loadTokenConfig() {
    if (!apiConfigured()) return;
    const cfg = await fetchTokenConfig();
    if (this.#dead()) return;
    this.#tokenConfig = cfg;
    if (cfg.enabled) this.#tokenBalance = await fetchTokenBalance(netManager.wallet);
  }

  /** True only when the $TOKEN feature is live AND the wallet can sign transactions. */
  #tokenPayable() {
    return !!this.#tokenConfig?.enabled && canPayToken();
  }

  /** Draws 4 emerald L-shaped corner brackets around a w×h panel centered at (x,y). */
  #cornerBrackets(x, y, w, h) {
    const g = this.add.graphics();
    g.lineStyle(3, C.emeraldNum, 0.9);
    const L = 24;
    const l = x - w / 2;
    const r = x + w / 2;
    const t = y - h / 2;
    const b = y + h / 2;
    const corner = (cxp, cyp, dx, dy) => {
      g.beginPath();
      g.moveTo(cxp + dx * L, cyp);
      g.lineTo(cxp, cyp);
      g.lineTo(cxp, cyp + dy * L);
      g.strokePath();
    };
    corner(l, t, 1, 1);
    corner(r, t, -1, 1);
    corner(l, b, 1, -1);
    corner(r, b, -1, -1);
  }

  /**
   * Header rows 2 + 3, laid out cleanly with no overlap.
   * @param {number} left panel left edge x
   * @param {number} right panel right edge x
   * @param {number} rowTabs y for the tabs + close row
   * @param {number} rowFilters y for the filter chips row
   */
  #buildTopBar(left, right, rowTabs, rowFilters) {
    const narrow = this.#narrow;
    const tf = narrow ? '14px' : '20px';
    // Lay the tabs out LEFT-TO-RIGHT by their MEASURED width + a gap, so 5 tabs never overlap
    // (the old fixed-offset layout collided — "VAULT" ran into "MY ORDERS"). Auto-fits any panel width.
    const gap = narrow ? 9 : 18;
    let cx = left + (narrow ? 10 : 22);
    const place = (label, view) => {
      const t = this.#tab(label, cx, rowTabs, () => this.#switchView(view), () => this.#view === view, tf);
      const nx = cx + t.width / 2; // shift so the tab's LEFT edge sits at the running cursor
      t.setX(nx);
      t.getData('underline')?.setX(nx);
      cx = nx + t.width / 2 + gap;
      return t;
    };
    place('SHOP', 'grid');
    place('GACHA', 'gacha');
    const topupTab = place(narrow ? 'TOP' : 'TOP UP', 'topup');
    // subtle gold "promo" pip above the TOP UP tab so players notice the (discounted) cash shop
    this.add.circle(topupTab.x + topupTab.width / 2 + 6, rowTabs - 9, 4, C.goldNum).setOrigin(0.5).setDepth(5);
    place(narrow ? 'VLT' : 'VAULT', 'vault');
    place(narrow ? 'ORD' : 'ORDERS', 'orders');

    // close (X) button (right of row 2)
    this.#textButton(narrow ? '✕' : '✕  CLOSE', right - (narrow ? 26 : 60), rowTabs, () => this.#close(), C.sub);

    // Sort + refresh controls (right of row 3, grid view only). The live CC catalog rotates often, so
    // players asked for a Refresh + a price sort instead of fixed budget chips.
    // Refresh re-pulls the live CC catalog. (Sorting/paging moved to the toolbar over the grid;
    // the server returns cards value-sorted, so a separate sort chip is no longer needed.)
    const chipW = narrow ? 80 : 112;
    const fx = right - (narrow ? 12 : 24) - chipW / 2;
    const refreshChip = this.#chip(narrow ? '↻' : '↻ REFRESH', fx, rowFilters, () => void this.#loadCards(), false, chipW);
    this.#filterChips = [{ btn: refreshChip }];
  }

  /** @type {{btn:{bg:Phaser.GameObjects.Rectangle,txt:Phaser.GameObjects.Text}}[]} */
  #filterChips = [];
  /** @type {'asc'|'desc'} price sort direction (asc = cheapest first) */
  #sortDir = 'asc';

  #chip(label, x, y, onClick, active, width = 100) {
    const bg = this.add
      .rectangle(x, y, width, 32, active ? C.emeraldNum : C.panel, active ? 0.9 : 0.9)
      .setStrokeStyle(2, active ? C.goldNum : C.panelStrokeNum, active ? 1 : 0.8)
      .setInteractive({ useHandCursor: true });
    const txt = this.add
      .text(x, y, label, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '16px',
        color: active ? '#03210f' : C.text,
      })
      .setOrigin(0.5);
    bg.on('pointerover', () => bg.setStrokeStyle(2, C.goldNum, 1));
    bg.on('pointerout', () => bg.setStrokeStyle(2, active ? C.goldNum : C.panelStrokeNum, active ? 1 : 0.8));
    bg.on('pointerdown', onClick);
    return { bg, txt };
  }

  #tab(label, x, y, onClick, isActive, fontSize = '22px') {
    const active = isActive();
    const txt = this.add
      .text(x, y, label, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize,
        color: active ? C.gold : C.sub,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    const underline = this.add.rectangle(x, y + 18, txt.width, 3, C.goldNum, active ? 1 : 0).setOrigin(0.5);
    txt.setData('underline', underline);
    txt.on('pointerover', () => txt.setColor(C.gold));
    txt.on('pointerout', () => txt.setColor(isActive() ? C.gold : C.sub));
    txt.on('pointerdown', onClick);
    this.#tabs.push({ txt, isActive });
    return txt;
  }

  /** @type {{txt:Phaser.GameObjects.Text,isActive:()=>boolean}[]} */
  #tabs = [];

  #textButton(label, x, y, onClick, color) {
    const txt = this.add
      .text(x, y, label, { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '18px', color })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    txt.on('pointerover', () => txt.setColor(C.gold));
    txt.on('pointerout', () => txt.setColor(color));
    txt.on('pointerdown', onClick);
    return txt;
  }

  #refreshTabs() {
    this.#tabs.forEach(({ txt, isActive }) => {
      const active = isActive();
      txt.setColor(active ? C.gold : C.sub);
      const u = txt.getData('underline');
      if (u) u.setAlpha(active ? 1 : 0);
    });
  }

  #switchView(view) {
    if (this.#view === view) return;
    this.#view = view;
    this.#refreshTabs();
    this.#filterChips.forEach((f) => f.btn.bg.setVisible(view === 'grid'));
    this.#filterChips.forEach((f) => f.btn.txt.setVisible(view === 'grid'));
    this.#discountBadge?.setVisible(view === 'grid'); // holder badge only meaningful in the grid
    if (this.#toolbarEl) this.#toolbarEl.style.display = view === 'grid' ? 'flex' : 'none';
    void this.#renderView(view);
  }

  /** Render the active tab, catching BOTH sync and async errors so a single tab's failure can never
   *  bubble to the global "Script error" overlay (reported when opening SHOP/TOP UP/GACHA). The real
   *  cause is logged to the console (unmasked) and the tab shows a friendly retry message. */
  async #renderView(view) {
    try {
      if (view === 'grid') await this.#loadCards();
      else if (view === 'gacha') await this.#renderGacha();
      else if (view === 'topup') await this.#renderTopup();
      else if (view === 'vault') await this.#renderVault();
      else await this.#loadOrders();
    } catch (err) {
      console.error('[card-shop] tab render failed:', view, err);
      this.#destroyDom();
      this.#showStatus('This tab hit an error — tap REFRESH or try another tab.');
    }
  }

  #applyFilter(v) {
    if (this.#maxUsd === v) return;
    this.#maxUsd = v;
    // recolor chips
    this.#filterChips.forEach((f) => {
      const active = f.v === v;
      f.btn.bg.setFillStyle(active ? C.emeraldNum : C.panel, 0.9);
      f.btn.bg.setStrokeStyle(2, active ? C.goldNum : C.panelStrokeNum, active ? 1 : 0.8);
      f.btn.txt.setColor(active ? '#03210f' : C.text);
    });
    void this.#loadCards();
  }

  async #refreshGold() {
    const wallet = netManager.wallet;
    const player = await fetchPlayer(wallet);
    if (this.#dead()) return;
    if (player && typeof player.gold === 'number') {
      dataManager.setGold(player.gold);
    }
    this.#gold = dataManager.getGold();
    this.#goldText?.setText(`${this.#gold}`);
  }

  /**
   * Header badge shown ONLY when the connected wallet earns a $MONSTARA holder discount.
   * Sits at the panel's right edge on the title row (mirroring the GOLD chip on the left), so it
   * never collides with the centered title or the tabs/close/filter rows below. Rebuilt each load.
   */
  #renderDiscountBadge() {
    this.#discountBadge?.destroy();
    this.#discountBadge = undefined;
    if (!(this.#holderDiscountPct > 0)) return;

    const w = this.scale.width;
    const right = w / 2 + this.#panelW / 2;
    const ROW1 = 36;
    const label = `🪙 $MONSTARA HOLDER  −${this.#holderDiscountPct}%`;

    const txt = this.add
      .text(0, 0, label, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '16px',
        color: C.gold,
      })
      .setOrigin(0.5);
    const padX = 16;
    const bw = txt.width + padX * 2;
    const bg = this.add
      .rectangle(0, 0, bw, 36, C.panel, 0.95)
      .setStrokeStyle(2, C.emeraldNum, 0.9)
      .setOrigin(0.5);
    bg.setFillStyle(0x06231a, 0.95); // emerald-tinted glass

    const container = this.add.container(right - 8 - bw / 2, ROW1, [bg, txt]);
    container.setDepth(50);
    this.#discountBadge = container;
  }

  // ── SHOP grid ──

  async #loadCards() {
    this.#destroyDom();
    this.#gridContainer?.removeAll(true);
    this.#loading = true;

    if (!apiConfigured()) {
      this.#showStatus('Card Shop is offline — connect to the server to redeem cards.');
      return;
    }
    this.#showStatus('Loading cards…');

    const result = await fetchShopCards({
      category: this.#category,
      search: this.#search,
      page: this.#page,
      wallet: netManager.wallet,
    });
    this.#loading = false;
    if (this.#dead() || this.#view !== 'grid') return; // closed/switched away while loading
    this.#cards = result.cards;
    this.#totalPages = result.totalPages;
    this.#page = result.page; // server clamps to a valid page
    this.#holderDiscountPct = result.holderDiscountPct;
    this.#renderDiscountBadge();
    if (this.#pagerLabel) this.#pagerLabel.textContent = `${result.page + 1}/${result.totalPages}`;

    if (result.cards.length === 0) {
      this.#showStatus(
        this.#search
          ? `No "${this.#search}" cards in ${this.#category}. Try another search or category.`
          : 'No cards available right now. Check back soon.'
      );
      return;
    }
    this.#hideStatus();
    this.#renderGrid();
  }

  /**
   * Persistent shop toolbar over the top of the panel: category tabs (Pokémon / One Piece / Sport),
   * a name search box, and a prev/next pager. Built once (survives card reloads so typing isn't lost);
   * grid-view only. The card grid below is offset down to clear it.
   */
  #buildToolbar() {
    const b = this.game.canvas.getBoundingClientRect();
    const sx = b.width / this.scale.width;
    const sy = b.height / this.scale.height;
    const bar = document.createElement('div');
    bar.style.cssText = [
      'position:fixed',
      `left:${b.left + (this.#panelX - this.#panelW / 2 + 16) * sx}px`,
      `top:${b.top + (this.#panelY - this.#panelH / 2 + 8) * sy}px`,
      `width:${(this.#panelW - 32) * sx}px`,
      'display:flex',
      'gap:8px',
      'align-items:center',
      'flex-wrap:wrap',
      'z-index:61',
      'font-family:system-ui,sans-serif',
    ].join(';');
    const CATS = [
      ['Pokemon', 'Pokémon'],
      ['One Piece', 'One Piece'],
      ['Sports', 'Sport'],
    ];
    this.#catBtns = [];
    for (const [cat, label] of CATS) {
      const el = document.createElement('button');
      el.textContent = label;
      el.style.cssText = this.#catStyle(cat === this.#category);
      el.onclick = () => {
        if (this.#category === cat) return;
        this.#category = cat;
        this.#search = '';
        if (this.#searchEl) this.#searchEl.value = '';
        this.#page = 0;
        this.#updateCatStyles();
        void this.#loadCards();
      };
      bar.appendChild(el);
      this.#catBtns.push({ cat, el });
    }
    const search = document.createElement('input');
    search.type = 'text';
    search.placeholder = 'Search cards…';
    search.value = this.#search;
    search.style.cssText = [
      'flex:1',
      'min-width:110px',
      'padding:8px 12px',
      'border-radius:10px',
      'border:1px solid rgba(52,211,153,0.45)',
      'background:rgba(8,18,12,0.85)',
      'color:#eafff5',
      'outline:none',
      'font-size:14px',
    ].join(';');
    search.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        this.#search = search.value.trim();
        this.#page = 0;
        void this.#loadCards();
      }
    });
    this.#searchEl = search;
    bar.appendChild(search);
    const mkBtn = (label, fn) => {
      const x = document.createElement('button');
      x.textContent = label;
      x.style.cssText = this.#pagerStyle();
      x.onclick = fn;
      return x;
    };
    const prev = mkBtn('◀', () => {
      if (this.#page > 0) {
        this.#page--;
        void this.#loadCards();
      }
    });
    const lbl = document.createElement('span');
    lbl.style.cssText = 'color:#bda886;font-size:13px;min-width:46px;text-align:center';
    lbl.textContent = '1/1';
    const next = mkBtn('▶', () => {
      if (this.#page < this.#totalPages - 1) {
        this.#page++;
        void this.#loadCards();
      }
    });
    this.#pagerLabel = lbl;
    bar.appendChild(prev);
    bar.appendChild(lbl);
    bar.appendChild(next);
    document.body.appendChild(bar);
    this.#toolbarEl = bar;
  }

  /** @param {boolean} active */
  #catStyle(active) {
    return [
      'padding:7px 12px',
      'border-radius:10px',
      'cursor:pointer',
      'font-size:14px',
      'font-weight:600',
      `border:1px solid ${active ? '#f5b23e' : 'rgba(52,211,153,0.4)'}`,
      `background:${active ? '#34d399' : 'rgba(8,18,12,0.85)'}`,
      `color:${active ? '#03210f' : '#eafff5'}`,
    ].join(';');
  }
  #pagerStyle() {
    return [
      'width:34px',
      'height:34px',
      'border-radius:8px',
      'cursor:pointer',
      'font-size:14px',
      'border:1px solid rgba(52,211,153,0.4)',
      'background:rgba(8,18,12,0.85)',
      'color:#eafff5',
    ].join(';');
  }
  #updateCatStyles() {
    for (const { cat, el } of this.#catBtns) el.style.cssText = this.#catStyle(cat === this.#category);
  }

  #renderGrid() {
    // DOM scroll container positioned over the panel interior, offset DOWN to clear the toolbar.
    const rect = this.#panelDomRect(40);
    const sy = this.game.canvas.getBoundingClientRect().height / this.scale.height;
    const toolbarH = 50 * sy;
    rect.top += toolbarH;
    rect.height = Math.max(120, rect.height - toolbarH);
    const scroll = document.createElement('div');
    scroll.style.cssText = [
      'position:fixed',
      `left:${rect.left}px`,
      `top:${rect.top}px`,
      `width:${rect.width}px`,
      `height:${rect.height}px`,
      'overflow-y:auto',
      'overflow-x:hidden',
      'display:grid',
      // bigger cells → larger, clearer slab images (fewer columns, more breathing room). On a narrow
      // phone, drop the min column width so the grid collapses to ONE column instead of clipping a
      // 300px-wide card; the auto-row height shrinks to match so the card isn't stretched.
      this.#narrow
        ? 'grid-template-columns:repeat(auto-fill,minmax(150px,1fr))'
        : 'grid-template-columns:repeat(auto-fill,minmax(300px,1fr))',
      // FORCE each row tall so cards render large (auto rows were collapsing to a thin strip).
      this.#narrow ? 'grid-auto-rows:300px' : 'grid-auto-rows:360px',
      'align-items:stretch',
      'gap:22px',
      'padding:10px 18px 12px 12px',
      'box-sizing:border-box',
      'z-index:60',
      'scrollbar-width:thin',
      'scrollbar-color:#34d399 rgba(255,255,255,0.06)',
    ].join(';');
    scroll.id = 'mn-cardshop-scroll';
    // Visible "slider" scrollbar (WebKit) so it's obvious the grid scrolls.
    const sbStyle = document.createElement('style');
    sbStyle.textContent =
      '#mn-cardshop-scroll::-webkit-scrollbar{width:12px}' +
      '#mn-cardshop-scroll::-webkit-scrollbar-track{background:rgba(255,255,255,0.05);border-radius:8px}' +
      '#mn-cardshop-scroll::-webkit-scrollbar-thumb{background:linear-gradient(#34d399,#0f9b6c);border-radius:8px;border:2px solid rgba(8,18,12,0.9)}' +
      '#mn-cardshop-scroll::-webkit-scrollbar-thumb:hover{background:#f5b23e}';
    document.head.appendChild(sbStyle);
    document.body.appendChild(scroll);
    this.#scrollEl = scroll;
    this.#domEls.push(scroll);
    this.#domEls.push(sbStyle);

    for (const card of this.#cards) {
      scroll.appendChild(this.#cardEl(card));
    }
  }

  /** @param {import('../net/api.js').ShopCard} card */
  #cardEl(card) {
    const affordable = this.#gold >= card.priceGold;
    const el = document.createElement('div');
    el.style.cssText = [
      'display:flex',
      'flex-direction:column',
      'height:100%', // fill the 360px grid row so the image area (flex:1) is tall
      'border-radius:14px',
      'overflow:hidden',
      'background:rgba(8,18,12,0.92)',
      `border:1px solid ${affordable ? 'rgba(52,211,153,0.55)' : 'rgba(120,140,128,0.35)'}`,
      'cursor:pointer',
      'transition:transform .12s ease, border-color .12s ease',
      affordable ? '' : 'opacity:0.72',
    ].join(';');
    el.onmouseenter = () => {
      el.style.transform = 'translateY(-3px)';
      el.style.borderColor = '#f5b23e';
    };
    el.onmouseleave = () => {
      el.style.transform = 'none';
      el.style.borderColor = affordable ? 'rgba(52,211,153,0.55)' : 'rgba(120,140,128,0.35)';
    };
    el.onclick = () => this.#openConfirm(card);

    // image
    const imgWrap = document.createElement('div');
    // Explicit height (aspect-ratio alone was collapsing the cell to a thin strip in this grid) so
    // each card image renders large. object-fit:contain shows the WHOLE graded card, not a crop.
    imgWrap.style.cssText =
      'width:100%;flex:1 1 auto;min-height:0;background:#0a1410;display:flex;align-items:center;justify-content:center;overflow:hidden;';
    const img = document.createElement('img');
    img.src = card.imageUrl;
    img.alt = card.name;
    img.loading = 'lazy';
    img.referrerPolicy = 'no-referrer';
    img.style.cssText = 'max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;';
    img.onerror = () => {
      img.style.display = 'none';
      imgWrap.textContent = '🃏';
      imgWrap.style.fontSize = '48px';
    };
    imgWrap.appendChild(img);

    // grade badge over the image
    const badge = document.createElement('div');
    badge.textContent = `${card.grader} ${card.grade}`;
    badge.style.cssText =
      'position:relative;align-self:flex-start;margin:-32px 0 0 10px;padding:4px 10px;border-radius:9px;background:rgba(5,16,10,0.85);border:1px solid rgba(52,211,153,0.6);color:#aef0c4;font:600 13px system-ui;white-space:nowrap;';

    // body
    const body = document.createElement('div');
    body.style.cssText = 'padding:10px 14px 14px;display:flex;flex-direction:column;gap:5px;';
    const name = document.createElement('div');
    name.textContent = card.name;
    name.style.cssText =
      'color:#eafff2;font:600 16px system-ui;line-height:1.25;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:40px;';
    const usd = document.createElement('div');
    usd.textContent = `$${card.valueUsd} value`;
    usd.style.cssText = 'color:#9fc4ad;font:500 14px system-ui;';
    const price = document.createElement('div');
    price.style.cssText = `color:${affordable ? '#f5b23e' : '#ff9a9a'};font:700 20px system-ui;margin-top:3px;display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;`;
    const priceNow = document.createElement('span');
    priceNow.textContent = `${card.priceGold.toLocaleString()} Gold`;
    price.appendChild(priceNow);
    // When a holder discount is active, show the original (full) price struck-through next to it.
    if (this.#holderDiscountPct > 0) {
      const original = Math.round(card.priceGold / (1 - this.#holderDiscountPct / 100));
      if (original > card.priceGold) {
        const was = document.createElement('span');
        was.textContent = `${original.toLocaleString()}`;
        was.style.cssText = 'color:#7d9c8a;font:500 14px system-ui;text-decoration:line-through;';
        price.appendChild(was);
      }
    }
    if (!affordable) {
      const need = document.createElement('div');
      need.textContent = `Need ${(card.priceGold - this.#gold).toLocaleString()} more`;
      need.style.cssText = 'color:#ff9a9a;font:500 13px system-ui;';
      body.append(name, usd, price, need);
    } else {
      body.append(name, usd, price);
    }

    el.append(imgWrap, badge, body);
    return el;
  }

  // ── Confirm + shipping form ──

  /** @param {import('../net/api.js').ShopCard} card */
  #openConfirm(card) {
    this.#destroyDom();
    const wallet = netManager.wallet;
    const affordable = this.#gold >= card.priceGold;

    const overlay = this.#modalOverlay();
    const panel = this.#modalPanel(overlay, 520);

    panel.appendChild(this.#h2('Confirm Redemption'));

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:14px;margin-bottom:12px;';
    const img = document.createElement('img');
    img.src = card.imageUrl;
    img.referrerPolicy = 'no-referrer';
    img.style.cssText = 'width:96px;height:128px;object-fit:cover;border-radius:10px;background:#0a1410;border:1px solid rgba(52,211,153,0.4);';
    img.onerror = () => (img.style.display = 'none');
    const info = document.createElement('div');
    info.style.cssText = 'flex:1;display:flex;flex-direction:column;gap:6px;';
    info.innerHTML =
      `<div style="color:#eafff2;font:600 15px system-ui;line-height:1.25">${this.#esc(card.name)}</div>` +
      `<div style="color:#aef0c4;font:500 13px system-ui">${this.#esc(card.grader)} ${this.#esc(card.grade)} · $${card.valueUsd} value</div>` +
      `<div style="color:#f5b23e;font:700 20px system-ui;margin-top:4px">${card.priceGold.toLocaleString()} Gold</div>` +
      `<div style="color:${affordable ? '#9fc4ad' : '#ff9a9a'};font:500 13px system-ui">Your balance: ${this.#gold.toLocaleString()} Gold</div>`;
    row.append(img, info);
    panel.appendChild(row);

    if (!wallet) {
      panel.appendChild(this.#note('Connect your wallet to redeem cards.', '#ff9a9a'));
      panel.appendChild(this.#modalButtons(overlay, [{ label: 'Close', onClick: () => this.#backToGrid(overlay) }]));
      return;
    }
    if (!affordable) {
      panel.appendChild(this.#note(`You need ${(card.priceGold - this.#gold).toLocaleString()} more Gold — win battles, or top up instantly.`, '#ff9a9a'));
      panel.appendChild(
        this.#modalButtons(overlay, [
          { label: 'Back', onClick: () => this.#backToGrid(overlay) },
          { label: 'Top up Gold →', primary: true, onClick: () => { this.#closeOverlay(overlay); this.#switchView('topup'); } },
        ])
      );
      return;
    }

    // ── Pay-with chooser (Gold | $TOKEN) — only when the $TOKEN feature is live + signable. ──
    /** @type {import('@mn/shared').PayWith} */
    let payWith = 'gold';
    const tokenPrice = card.priceToken ?? 0;
    let payNote;
    if (this.#tokenPayable() && tokenPrice > 0) {
      panel.appendChild(this.#h3('Pay with'));
      const payRow = document.createElement('div');
      payRow.style.cssText = 'display:flex;gap:10px;margin-bottom:8px;';
      const goldOpt = this.#payOption('🪙 Gold', `${card.priceGold.toLocaleString()} Gold`, true);
      const tokenOpt = this.#payOption('⛓️ $TOKEN', `${tokenPrice.toLocaleString()} $TOKEN`, false);
      payRow.append(goldOpt, tokenOpt);
      payNote = this.#note('', '#9fc4ad');
      const selectPay = (v) => {
        payWith = v;
        goldOpt.dataset.on = v === 'gold' ? '1' : '';
        tokenOpt.dataset.on = v === 'token' ? '1' : '';
        for (const el of [goldOpt, tokenOpt]) {
          const on = el.dataset.on === '1';
          el.style.borderColor = on ? '#f5b23e' : 'rgba(52,211,153,0.4)';
          el.style.background = on ? 'rgba(245,178,62,0.10)' : 'rgba(8,18,12,0.85)';
        }
        const cost = v === 'token'
          ? `${tokenPrice.toLocaleString()} $TOKEN (balance ${this.#tokenBalance.toLocaleString()})`
          : `${card.priceGold.toLocaleString()} Gold (balance ${this.#gold.toLocaleString()})`;
        payNote.textContent = `You'll pay ${cost}`;
      };
      goldOpt.onclick = () => selectPay('gold');
      tokenOpt.onclick = () => selectPay('token');
      panel.appendChild(payRow);
      panel.appendChild(payNote);
      selectPay('gold');
    }

    // ── Delivery method chooser (shown BEFORE the shipping form) ──
    // Default to on-chain: simplest path, no PII — the card NFT is sent to the connected wallet.
    panel.appendChild(this.#h3('How should we deliver it?'));
    /** @type {import('../net/api.js').DeliveryMethod} */
    let deliveryMethod = 'onchain';
    const shortWallet = `${wallet.slice(0, 4)}…${wallet.slice(-4)}`;

    // On-chain destination note (replaces the shipping form when on-chain is selected).
    const onchainNote = this.#note(`Card will be transferred on-chain to ${shortWallet}`, '#aef0c4');
    onchainNote.style.textAlign = 'left';

    // Shipping form (revealed only when IRL is selected).
    const shipWrap = document.createElement('div');
    const shipHeading = this.#h3('Shipping details');
    const fields = {
      name: this.#field('Full name'),
      address: this.#field('Street address'),
      city: this.#field('City'),
      postal: this.#field('Postal / ZIP code'),
      country: this.#field('Country'),
    };
    shipWrap.appendChild(shipHeading);
    Object.values(fields).forEach((f) => shipWrap.appendChild(f.wrap));

    // Twitter/X handle — shown for BOTH delivery methods so the team can reach the player to confirm
    // and arrange fulfilment (on-chain or IRL).
    const twitterField = this.#field('Twitter / X handle (so the team can reach you)');

    const errLine = this.#note('', '#ff9a9a');

    // Two selectable option cards.
    const optWrap = document.createElement('div');
    optWrap.style.cssText = `display:flex;gap:10px;margin-bottom:12px;${this.#narrow ? 'flex-direction:column;' : ''}`;
    /** @param {{ icon:string, title:string, sub:string, value:import('../net/api.js').DeliveryMethod }} o */
    const makeOption = (o) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.dataset.value = o.value;
      el.style.cssText = [
        'flex:1', 'text-align:left', 'cursor:pointer', 'box-sizing:border-box',
        'padding:12px 14px', 'border-radius:12px', 'background:rgba(8,18,12,0.85)',
        'border:1px solid rgba(52,211,153,0.4)', 'transition:border-color .15s,background .15s',
      ].join(';');
      el.innerHTML =
        `<div style="color:#eafff2;font:600 14px system-ui;margin-bottom:2px">${o.icon} ${this.#esc(o.title)}</div>` +
        `<div style="color:#9fc4ad;font:500 12px system-ui">${this.#esc(o.sub)}</div>`;
      el.onclick = () => select(o.value);
      return el;
    };
    const onchainOpt = makeOption({ icon: '⛓️', title: 'On-chain', sub: 'Sent to my Solana wallet', value: 'onchain' });
    const irlOpt = makeOption({ icon: '📦', title: 'Ship to me (IRL)', sub: 'Physical delivery', value: 'irl' });
    optWrap.append(onchainOpt, irlOpt);

    /** @param {import('../net/api.js').DeliveryMethod} v */
    const select = (v) => {
      deliveryMethod = v;
      for (const el of [onchainOpt, irlOpt]) {
        const on = el.dataset.value === v;
        el.style.borderColor = on ? '#f5b23e' : 'rgba(52,211,153,0.4)';
        el.style.background = on ? 'rgba(245,178,62,0.10)' : 'rgba(8,18,12,0.85)';
      }
      onchainNote.style.display = v === 'onchain' ? 'block' : 'none';
      shipWrap.style.display = v === 'irl' ? 'block' : 'none';
      errLine.textContent = '';
    };

    panel.appendChild(optWrap);
    panel.appendChild(onchainNote);
    panel.appendChild(shipWrap);
    panel.appendChild(twitterField.wrap);
    panel.appendChild(errLine);
    select('onchain'); // default

    const submit = { label: `Redeem for ${card.priceGold.toLocaleString()} Gold`, primary: true, onClick: async () => {} };
    let busy = false;
    submit.onClick = async () => {
      if (busy) return;
      /** @type {{ wallet:string, card:any, deliveryMethod:import('../net/api.js').DeliveryMethod, shipping?:any, twitter?:string, payWith?:import('@mn/shared').PayWith, tokenPaymentSig?:string }} */
      const payload = {
        wallet,
        card: { mint: card.mint, name: card.name, imageUrl: card.imageUrl, valueUsd: card.valueUsd },
        deliveryMethod,
      };
      // Twitter handle is required (both delivery methods) so the team can always reach the player.
      const twitter = twitterField.input.value.trim();
      if (!twitter) {
        errLine.style.color = '#ff9a9a';
        errLine.textContent = 'Please add your Twitter / X handle so the team can reach you.';
        return;
      }
      payload.twitter = twitter;
      if (deliveryMethod === 'irl') {
        const shipping = {
          name: fields.name.input.value.trim(),
          address: fields.address.input.value.trim(),
          city: fields.city.input.value.trim(),
          postal: fields.postal.input.value.trim(),
          country: fields.country.input.value.trim(),
        };
        if (Object.values(shipping).some((v) => !v)) {
          errLine.style.color = '#ff9a9a';
          errLine.textContent = 'All shipping fields are required.';
          return;
        }
        payload.shipping = shipping;
      }
      busy = true;
      // $TOKEN path: sign+send the player→treasury transfer FIRST, then attach its signature.
      if (payWith === 'token' && this.#tokenPayable() && tokenPrice > 0) {
        if (this.#tokenBalance < tokenPrice) {
          busy = false;
          errLine.style.color = '#ff9a9a';
          errLine.textContent = `Not enough $TOKEN — you need ${tokenPrice.toLocaleString()}.`;
          return;
        }
        errLine.style.color = '#9fc4ad';
        errLine.textContent = 'Approve the $TOKEN payment in your wallet…';
        const rpcUrl = import.meta.env.VITE_RPC_URL || '';
        const sig = await payTokenToTreasury(this.#tokenConfig, tokenPrice, rpcUrl);
        if (!sig) {
          busy = false;
          errLine.style.color = '#ff9a9a';
          errLine.textContent = 'Payment was not completed. Please try again.';
          return;
        }
        payload.payWith = 'token';
        payload.tokenPaymentSig = sig;
      }
      errLine.style.color = '#9fc4ad';
      errLine.textContent = 'Placing order…';
      const result = await placeShopOrder(payload);
      busy = false;
      if (result.ok === true) {
        // advance the onboarding objective tracker ("Order a real card")
        dataManager.recordCardOrdered();
        await this.#refreshGold();
        this.#showSuccess(overlay, card);
        return;
      }
      const reason = result.ok === false ? result.reason : 'error';
      errLine.style.color = '#ff9a9a';
      errLine.textContent =
        reason === 'insufficient'
          ? 'Not enough Gold — your balance changed. Win more battles!'
          : 'Could not place the order. Please try again.';
    };

    panel.appendChild(
      this.#modalButtons(overlay, [{ label: 'Cancel', onClick: () => this.#backToGrid(overlay) }, submit])
    );
  }

  #showSuccess(overlay, card) {
    overlay.innerHTML = '';
    const panel = this.#modalPanel(overlay, 440);
    const check = document.createElement('div');
    check.textContent = '✓';
    check.style.cssText =
      'width:64px;height:64px;border-radius:50%;background:rgba(52,211,153,0.15);border:2px solid #34d399;color:#34d399;font:700 34px system-ui;display:flex;align-items:center;justify-content:center;margin:0 auto 14px;';
    panel.appendChild(check);
    panel.appendChild(this.#h2('Order placed! 🎴'));
    panel.appendChild(
      this.#note(
        `Your order for "${card.name}" is in! The team will DM you on Twitter / X to confirm and arrange delivery. Track it anytime under "My Orders".`,
        '#aef0c4'
      )
    );
    panel.appendChild(
      this.#modalButtons(overlay, [
        { label: 'View My Orders', primary: true, onClick: () => { this.#closeOverlay(overlay); this.#switchView('orders'); } },
        { label: 'Keep Shopping', onClick: () => this.#backToGrid(overlay) },
      ])
    );
  }

  // ── Gacha packs view ──────────────────────────────────────────────────────────────────────────

  /** Render the gacha view: a category selector + the three value-tier packs, ordered for Gold. */
  // ── Top-Up: buy Gold / a Gold booster / a gacha pack with $MONSTARA or SOL (verified inbound). ──

  /** Fetch + cache the top-up config (live $TOKEN/SOL quotes). force=true bypasses the TTL cache. */
  async #ensureTopupConfig(force = false) {
    const ttl = this.#topupCfg?.quoteTtlMs ?? 60_000;
    if (!force && this.#topupCfg && Date.now() - this.#topupAt < ttl) return this.#topupCfg;
    const cfg = await fetchTopupConfig();
    this.#topupCfg = cfg;
    this.#topupAt = Date.now();
    return cfg;
  }

  #renderTopup() {
    void (async () => {
      try {
        await this.#renderTopupInner();
      } catch (err) {
        console.error('[card-shop] top-up render failed:', err);
        this.#destroyDom();
        this.#showStatus('Top-Up is temporarily unavailable — try again.');
      }
    })();
  }

  async #renderTopupInner() {
    this.#destroyDom();
    this.#showStatus('Loading…');
    const cfg = await this.#ensureTopupConfig();
    if (this.#dead() || this.#view !== 'topup') return; // closed/switched away while loading
    this.#hideStatus();
    if (!cfg.enabled) {
      this.#showStatus('Buying Gold with $MONSTARA / SOL is coming soon.');
      return;
    }
    const rect = this.#panelDomRect(28);
    const wrap = document.createElement('div');
    wrap.style.cssText = [
      'position:fixed',
      `left:${rect.left}px`,
      `top:${rect.top}px`,
      `width:${rect.width}px`,
      `height:${rect.height}px`,
      'overflow-y:auto',
      'z-index:60',
      'display:flex',
      'flex-direction:column',
      'gap:16px',
      'padding:6px 16px 16px',
      'box-sizing:border-box',
      'font-family:system-ui,sans-serif',
    ].join(';');

    const intro = document.createElement('div');
    intro.style.cssText = 'color:#aef0c4;font:500 14px system-ui;line-height:1.4';
    const pr = cfg.prices;
    intro.innerHTML =
      'Top up with <b>$MONSTARA</b> or <b>SOL</b> — instant Gold, a Gold-earn booster, or a gacha pack. Amounts track the live market price.' +
      (pr
        ? `<div style="color:#7fae93;font:500 11px system-ui;margin-top:4px">live · SOL $${pr.sol.toFixed(2)} · MONSTARA $${pr.token.toPrecision(3)}</div>`
        : '');
    wrap.appendChild(intro);

    const grid = (els) => {
      const g = document.createElement('div');
      g.style.cssText = `display:grid;gap:14px;${this.#narrow ? 'grid-template-columns:1fr' : 'grid-template-columns:repeat(3,1fr)'}`;
      els.forEach((e) => g.appendChild(e));
      return g;
    };

    // Gold packs
    wrap.appendChild(this.#topupSectionTitle('Gold packs'));
    wrap.appendChild(
      grid(
        (cfg.packs || []).map((p) =>
          this.#topupCard('🪙', `${p.gold.toLocaleString()} Gold`, `≈ $${p.usd}`, p, (rail) =>
            this.#openTopupPay({ kind: 'pack', id: p.id, label: `${p.gold.toLocaleString()} Gold`, get: p }, rail)
          )
        )
      )
    );

    // Gold booster
    if (cfg.booster) {
      const b = cfg.booster;
      wrap.appendChild(this.#topupSectionTitle('Gold booster (pay-to-win)'));
      wrap.appendChild(
        grid([
          this.#topupCard('⚡', `${b.multiplier}× Gold · ${b.battles} wins`, `≈ $${b.usd}`, b, (rail) =>
            this.#openTopupPay({ kind: 'booster', label: `${b.multiplier}× Gold booster`, get: b }, rail)
          ),
        ])
      );
    }

    // Gacha packs (shipped — opens the shipping form)
    if (cfg.gacha?.length) {
      wrap.appendChild(this.#topupSectionTitle('Gacha packs (mystery — shipped)'));
      const catRow = document.createElement('div');
      catRow.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap';
      for (const [cat, label] of [
        ['Pokemon', 'Pokémon'],
        ['One Piece', 'One Piece'],
        ['Sports', 'Sport'],
      ]) {
        const el = document.createElement('button');
        el.textContent = label;
        el.style.cssText = this.#catStyle(cat === this.#category);
        el.onclick = () => {
          if (this.#category !== cat) {
            this.#category = cat;
            void this.#renderTopup();
          }
        };
        catRow.appendChild(el);
      }
      wrap.appendChild(catRow);
      const ICON = { 50: '🎴', 250: '🎁', 2500: '💎' };
      wrap.appendChild(
        grid(
          cfg.gacha.map((g) =>
            this.#topupCard(ICON[g.tier] || '🎴', `€${g.tier} ${this.#esc(this.#category)} pack`, `≈ $${g.usd}`, g, (rail) =>
              this.#openGachaConfirm(g.tier, rail)
            )
          )
        )
      );
    }

    document.body.appendChild(wrap);
    this.#domEls.push(wrap);
  }

  #topupSectionTitle(t) {
    const e = document.createElement('div');
    e.textContent = t;
    e.style.cssText = 'color:#34d399;font:700 16px system-ui;margin-top:4px';
    return e;
  }

  // ── Card Vault: stake real cards for passive Gold ──
  #renderVault() {
    return this.#renderVaultInner().catch((err) => {
      console.error('[card-shop] vault render failed', err);
      this.#destroyDom();
      this.#showStatus('Card Vault is temporarily unavailable — try again.');
    });
  }

  async #renderVaultInner() {
    this.#destroyDom();
    this.#showStatus('Loading…');
    const wallet = netManager.wallet;
    const [stakes, cfg] = await Promise.all([fetchVaultStakes(wallet), fetchTopupConfig()]);
    if (this.#dead() || this.#view !== 'vault') return; // closed/switched away while loading
    this.#hideStatus();
    const treasury = cfg?.treasury || '';

    const rect = this.#panelDomRect(28);
    const wrap = document.createElement('div');
    wrap.style.cssText = [
      'position:fixed',
      `left:${rect.left}px`,
      `top:${rect.top}px`,
      `width:${rect.width}px`,
      `height:${rect.height}px`,
      'overflow-y:auto',
      'z-index:60',
      'display:flex',
      'flex-direction:column',
      'gap:14px',
      'padding:6px 16px 16px',
      'box-sizing:border-box',
      'font-family:system-ui,sans-serif',
    ].join(';');

    // The vault's fixed DOM overlay sits ABOVE the Phaser close button, so add a DOM Close here —
    // otherwise on desktop you couldn't exit the Vault without refreshing the page.
    const closeRow = document.createElement('div');
    closeRow.style.cssText = 'display:flex;justify-content:flex-end;margin:-2px 0 2px';
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕ Close';
    closeBtn.style.cssText =
      'padding:6px 14px;border-radius:10px;border:1px solid rgba(180,180,180,0.4);background:rgba(20,20,20,0.55);color:#cdd6dd;font:600 13px system-ui;cursor:pointer';
    closeBtn.onclick = () => this.#close();
    closeRow.appendChild(closeBtn);
    wrap.appendChild(closeRow);

    const intro = document.createElement('div');
    intro.style.cssText = 'color:#aef0c4;font:500 14px system-ui;line-height:1.4';
    intro.innerHTML =
      'Stake a real graded card with us and earn <b>passive Gold</b> every day. Your card stays safe in our vault — claim and request it back anytime.';
    wrap.appendChild(intro);

    const active = (stakes || []).filter((s) => s.status === 'active');
    const totalAccrued = active.reduce((a, s) => a + (s.accruedGold || 0), 0);
    if (active.length > 0) {
      const row = document.createElement('div');
      row.style.cssText =
        'display:flex;align-items:center;justify-content:space-between;gap:10px;background:rgba(8,18,12,0.85);border:1px solid rgba(52,211,153,0.45);border-radius:12px;padding:12px 14px';
      row.innerHTML = `<div style="color:#eafff2;font:600 14px system-ui">Unclaimed: <span style="color:#f5b23e">${totalAccrued.toLocaleString()} Gold</span></div>`;
      const claimBtn = document.createElement('button');
      claimBtn.textContent = totalAccrued > 0 ? 'CLAIM' : 'NOTHING YET';
      claimBtn.disabled = totalAccrued <= 0;
      claimBtn.style.cssText = `padding:8px 18px;border-radius:10px;font:700 14px system-ui;cursor:${totalAccrued > 0 ? 'pointer' : 'not-allowed'};border:1px solid ${totalAccrued > 0 ? '#34d399' : 'rgba(120,120,120,0.4)'};background:${totalAccrued > 0 ? '#34d399' : 'rgba(40,40,40,0.6)'};color:${totalAccrued > 0 ? '#03210f' : '#888'}`;
      if (totalAccrued > 0) {
        claimBtn.onclick = async () => {
          claimBtn.disabled = true;
          claimBtn.textContent = 'Claiming…';
          const r = await claimVaultGold(wallet);
          if (r && typeof r.gold === 'number') dataManager.setGold(r.gold);
          void this.#refreshGold();
          void this.#renderVault();
        };
      }
      row.appendChild(claimBtn);
      wrap.appendChild(row);
    }

    wrap.appendChild(this.#topupSectionTitle('Your staked cards'));
    if (!stakes || stakes.length === 0) {
      const none = document.createElement('div');
      none.style.cssText = 'color:#9fc4ad;font:500 13px system-ui';
      none.textContent = 'No cards staked yet.';
      wrap.appendChild(none);
    } else {
      stakes.forEach((s) => wrap.appendChild(this.#vaultStakeRow(s, wallet)));
    }

    // Stake-a-card form
    wrap.appendChild(this.#topupSectionTitle('Stake a card'));
    const inputCss =
      'width:100%;box-sizing:border-box;padding:9px 12px;border-radius:10px;border:1px solid rgba(52,211,153,0.4);background:rgba(8,18,12,0.85);color:#eafff5;outline:none;font:500 13px system-ui';
    const form = document.createElement('div');
    form.style.cssText =
      'background:rgba(8,18,12,0.85);border:1px solid rgba(52,211,153,0.4);border-radius:12px;padding:14px;display:flex;flex-direction:column;gap:9px';
    const help = document.createElement('div');
    help.style.cssText = 'color:#9fc4ad;font:500 12px system-ui;line-height:1.45';
    help.innerHTML = treasury
      ? `Send your card NFT to the vault wallet, then paste its mint + the transfer signature.<br><span style="color:#7fae93">Vault wallet: <span style="font-family:monospace;color:#aef0c4">${this.#esc(treasury)}</span></span>`
      : 'Vault is not available right now.';
    form.appendChild(help);
    const mintIn = document.createElement('input');
    mintIn.placeholder = 'Card mint address';
    mintIn.style.cssText = inputCss;
    const sigIn = document.createElement('input');
    sigIn.placeholder = 'Transfer signature (optional — speeds up verification)';
    sigIn.style.cssText = inputCss;
    const status = document.createElement('div');
    status.style.cssText = 'color:#9fc4ad;font:500 12px system-ui;min-height:14px';
    const stakeBtn = document.createElement('button');
    stakeBtn.textContent = 'STAKE CARD';
    stakeBtn.style.cssText =
      'margin-top:2px;padding:10px 14px;border-radius:10px;border:1px solid #34d399;background:#34d399;color:#03210f;font:700 14px system-ui;cursor:pointer';
    stakeBtn.onclick = async () => {
      const mint = mintIn.value.trim();
      if (!wallet) {
        status.style.color = '#ff9a9a';
        status.textContent = 'Connect your wallet first.';
        return;
      }
      if (mint.length < 32) {
        status.style.color = '#ff9a9a';
        status.textContent = 'Enter a valid card mint address.';
        return;
      }
      stakeBtn.disabled = true;
      status.style.color = '#9fc4ad';
      status.textContent = 'Registering your stake…';
      const res = await stakeVaultCard(wallet, mint, sigIn.value.trim() || undefined);
      stakeBtn.disabled = false;
      if (!res) {
        status.style.color = '#ff9a9a';
        status.textContent = 'Could not stake — check the mint/signature, or the card may already be staked.';
        return;
      }
      void this.#renderVault();
    };
    form.append(mintIn, sigIn, status, stakeBtn);
    wrap.appendChild(form);

    document.body.appendChild(wrap);
    this.#domEls.push(wrap);
  }

  /** One staked-card row: name / value / Gold-per-day / accrued + status, with an un-stake button. */
  #vaultStakeRow(s, wallet) {
    const el = document.createElement('div');
    el.style.cssText =
      'background:rgba(8,18,12,0.85);border:1px solid rgba(52,211,153,0.35);border-radius:12px;padding:12px 14px;display:flex;align-items:center;justify-content:space-between;gap:10px';
    const canUnstake = s.status === 'active' && s.canUnstakeAt && Date.now() >= Date.parse(s.canUnstakeAt);
    const statusLabel =
      s.status === 'active'
        ? `${(s.dailyGold || 0).toLocaleString()} Gold/day · +${(s.accruedGold || 0).toLocaleString()} unclaimed`
        : s.status === 'pending'
          ? 'Pending review'
          : s.status === 'unstake_requested'
            ? 'Return requested'
            : s.status;
    el.innerHTML =
      `<div style="min-width:0"><div style="color:#eafff2;font:600 14px system-ui;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${this.#esc(s.cardName || 'Graded card')}</div>` +
      `<div style="color:#9fc4ad;font:500 12px system-ui">$${s.valueUsd} · ${this.#esc(statusLabel)}</div></div>`;
    if (s.status === 'active') {
      const btn = document.createElement('button');
      btn.textContent = canUnstake ? 'Un-stake' : 'Locked';
      btn.disabled = !canUnstake;
      btn.title = canUnstake ? '' : `Unlocks ${s.canUnstakeAt ? new Date(s.canUnstakeAt).toLocaleDateString() : 'soon'}`;
      btn.style.cssText = `flex:none;padding:7px 12px;border-radius:9px;font:600 12px system-ui;cursor:${canUnstake ? 'pointer' : 'not-allowed'};border:1px solid ${canUnstake ? 'rgba(245,178,62,0.6)' : 'rgba(120,120,120,0.4)'};background:rgba(8,18,12,0.6);color:${canUnstake ? '#f5b23e' : '#888'}`;
      if (canUnstake) {
        btn.onclick = async () => {
          btn.disabled = true;
          btn.textContent = '…';
          await requestVaultUnstake(wallet, s.id);
          void this.#renderVault();
        };
      }
      el.appendChild(btn);
    }
    return el;
  }

  /** A top-up item card: icon + label + USD, with a "$MONSTARA" and a "SOL" pay button. `quote`
   *  carries {token, sol, usd}; `onPay(rail)` is called with 'token' | 'sol'. */
  #topupCard(icon, label, usd, quote, onPay) {
    const promoPct = this.#topupCfg?.promoPct || 0;
    const hasPromo = promoPct > 0 && !!quote.regularToken && quote.regularToken > quote.token;
    const card = document.createElement('div');
    card.style.cssText = [
      'position:relative',
      'display:flex',
      'flex-direction:column',
      'gap:8px',
      'padding:16px',
      'border-radius:14px',
      'background:rgba(8,18,12,0.85)',
      `border:1px solid ${hasPromo ? 'rgba(245,178,62,0.55)' : 'rgba(52,211,153,0.45)'}`,
      'align-items:center',
      'text-align:center',
    ].join(';');
    const badge = hasPromo
      ? `<div style="position:absolute;top:8px;right:8px;background:#f5b23e;color:#1a1206;font:800 10px system-ui;letter-spacing:.4px;padding:3px 7px;border-radius:999px;box-shadow:0 0 10px rgba(245,178,62,0.4)">−${promoPct}% LAUNCH</div>`
      : '';
    const usdLine =
      hasPromo && quote.regularUsd
        ? `<span style="text-decoration:line-through;opacity:.5">$${quote.regularUsd}</span> <span style="color:#f5b23e;font-weight:700">${this.#esc(usd)}</span>`
        : this.#esc(usd);
    card.innerHTML =
      badge +
      `<div style="font-size:34px">${icon}</div>` +
      `<div style="color:#eafff2;font:700 16px system-ui">${this.#esc(label)}</div>` +
      `<div style="color:#9fc4ad;font:500 12px system-ui">${usdLine}</div>`;
    const strike = 'opacity:.5;text-decoration:line-through;font-size:11px;font-weight:600';
    const mkBtn = (html, rail, bg, fg, border) => {
      const b = document.createElement('button');
      b.innerHTML = html;
      b.style.cssText = [
        'width:100%',
        'padding:9px 12px',
        'border-radius:10px',
        'cursor:pointer',
        'font:700 13px system-ui',
        `border:1px solid ${border}`,
        `background:${bg}`,
        `color:${fg}`,
      ].join(';');
      b.onclick = () => onPay(rail);
      return b;
    };
    const tokPre = hasPromo ? `<span style="${strike}">${quote.regularToken.toLocaleString()}</span> ` : '';
    const solPre = hasPromo ? `<span style="${strike}">${quote.regularSol}</span> ` : '';
    card.appendChild(mkBtn(`${tokPre}${quote.token.toLocaleString()} $MONSTARA`, 'token', '#34d399', '#03210f', '#34d399'));
    card.appendChild(mkBtn(`${solPre}${quote.sol} SOL`, 'sol', 'rgba(20,30,24,0.9)', '#eafff2', 'rgba(52,211,153,0.5)'));
    return card;
  }

  /** Pay for a Gold pack / booster with $TOKEN or SOL (verified inbound). item.kind = 'pack'|'booster'. */
  #openTopupPay(item, payWith) {
    void (async () => {
      this.#destroyDom();
      const wallet = netManager.wallet;
      const overlay = this.#modalOverlay();
      const panel = this.#modalPanel(overlay, 460);
      panel.appendChild(this.#h2(item.label));
      const back = () => this.#backToTopup(overlay);
      if (!wallet) {
        panel.appendChild(this.#note('Log in with your wallet first to pay with $MONSTARA or SOL.', '#ff9a9a'));
        panel.appendChild(this.#modalButtons(overlay, [{ label: 'Back', onClick: back }]));
        return;
      }
      if (!canPayToken()) {
        // Not a dead-end any more: let the player connect a sign-capable wallet right here. The
        // payment must come from their account wallet (the server binds the source to it).
        panel.appendChild(this.#note('Connect your wallet so it can sign the payment.', '#ffd9a0'));
        const status = this.#note('', '#9fc4ad');
        panel.appendChild(status);
        panel.appendChild(this.#modalButtons(overlay, [
          {
            label: 'Connect wallet',
            primary: true,
            onClick: async () => {
              status.textContent = 'Opening your wallet…';
              const ok = await connectPayProvider();
              if (ok) this.#openTopupPay(item, payWith);
              else status.textContent = 'No sign-capable wallet found — open/install Phantom, then try again.';
            },
          },
          { label: 'Back', onClick: back },
        ]));
        return;
      }
      const unit = payWith === 'token' ? '$MONSTARA' : 'SOL';
      const amt0 = payWith === 'token' ? item.get.token : item.get.sol;
      panel.appendChild(this.#note(`You'll pay ${amt0.toLocaleString()} ${unit} (≈ $${item.get.usd}).`, '#aef0c4'));
      const errLine = this.#note('', '#9fc4ad');
      panel.appendChild(errLine);
      let busy = false;
      const submit = {
        label: `Pay ${unit}`,
        primary: true,
        onClick: async () => {
          if (busy) return;
          busy = true;
          errLine.style.color = '#9fc4ad';
          errLine.textContent = 'Refreshing price…';
          const cfg = await this.#ensureTopupConfig(true); // fresh quote right before paying
          const fresh = item.kind === 'pack' ? (cfg.packs || []).find((p) => p.id === item.id) : cfg.booster;
          const amt = payWith === 'token' ? fresh?.token : fresh?.sol;
          if (!cfg.enabled || !amt) {
            busy = false;
            errLine.style.color = '#ff9a9a';
            errLine.textContent = 'Price unavailable — please try again.';
            return;
          }
          errLine.textContent = 'Approve the payment in your wallet…';
          const rpc = import.meta.env.VITE_RPC_URL || '';
          const sig =
            payWith === 'token'
              ? await payTokenToTreasury(cfg, amt, rpc)
              : await paySolToTreasury(cfg.treasury, amt, rpc);
          if (!sig) {
            busy = false;
            errLine.style.color = '#ff9a9a';
            errLine.textContent = 'Payment was not completed. Please try again.';
            return;
          }
          errLine.textContent = 'Verifying payment…';
          const res =
            item.kind === 'pack'
              ? await buyGoldPack(wallet, item.id, payWith, sig)
              : await buyGoldBooster(wallet, payWith, sig);
          busy = false;
          if (res.ok) {
            await this.#refreshGold();
            this.#showTopupSuccess(overlay, item, payWith);
            return;
          }
          errLine.style.color = '#ff9a9a';
          errLine.textContent = 'Payment sent but could not be credited — keep your transaction and contact the team.';
        },
      };
      panel.appendChild(this.#modalButtons(overlay, [{ label: 'Cancel', onClick: back }, submit]));
    })();
  }

  #showTopupSuccess(overlay, item, payWith) {
    overlay.innerHTML = '';
    const panel = this.#modalPanel(overlay, 420);
    const check = document.createElement('div');
    check.textContent = '✓';
    check.style.cssText =
      'width:64px;height:64px;border-radius:50%;background:rgba(52,211,153,0.15);border:2px solid #34d399;color:#34d399;font:700 34px system-ui;display:flex;align-items:center;justify-content:center;margin:0 auto 14px;';
    panel.appendChild(check);
    panel.appendChild(this.#h2(item.kind === 'booster' ? 'Booster active! ⚡' : 'Gold added! 🪙'));
    panel.appendChild(
      this.#note(
        `Paid with ${payWith === 'token' ? '$MONSTARA' : 'SOL'}. ${item.kind === 'booster' ? 'Your next wins earn boosted Gold.' : 'Your Gold balance is updated.'}`,
        '#aef0c4'
      )
    );
    panel.appendChild(this.#modalButtons(overlay, [{ label: 'Done', primary: true, onClick: () => this.#backToTopup(overlay) }]));
  }

  #backToTopup(overlay) {
    this.#closeOverlay(overlay);
    void this.#renderTopup();
  }

  #renderGacha() {
    try {
      this.#renderGachaInner();
    } catch (err) {
      // Never let a render throw bubble to the global "Script error" overlay (which masks the cause).
      console.error('[card-shop] gacha render failed:', err);
      this.#destroyDom();
      this.#showStatus('Gacha is temporarily unavailable — tap REFRESH or try again.');
    }
  }

  #renderGachaInner() {
    this.#destroyDom();
    this.#hideStatus();
    const rect = this.#panelDomRect(28);
    const wrap = document.createElement('div');
    wrap.style.cssText = [
      'position:fixed',
      `left:${rect.left}px`,
      `top:${rect.top}px`,
      `width:${rect.width}px`,
      `height:${rect.height}px`,
      'overflow-y:auto',
      'z-index:60',
      'display:flex',
      'flex-direction:column',
      'gap:14px',
      'padding:6px 16px 16px',
      'box-sizing:border-box',
      'font-family:system-ui,sans-serif',
    ].join(';');

    const intro = document.createElement('div');
    intro.style.cssText = 'color:#aef0c4;font:500 14px system-ui;line-height:1.4';
    intro.innerHTML =
      'Order a <b>mystery gacha pack</b> — the team opens it on CollectorCrypt and ships you the pull. Pick a card family and a value tier.';
    wrap.appendChild(intro);

    const catRow = document.createElement('div');
    catRow.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap';
    for (const [cat, label] of [
      ['Pokemon', 'Pokémon'],
      ['One Piece', 'One Piece'],
      ['Sports', 'Sport'],
    ]) {
      const el = document.createElement('button');
      el.textContent = label;
      el.style.cssText = this.#catStyle(cat === this.#category);
      el.onclick = () => {
        if (this.#category !== cat) {
          this.#category = cat;
          this.#renderGacha();
        }
      };
      catRow.appendChild(el);
    }
    wrap.appendChild(catRow);

    const packs = document.createElement('div');
    packs.style.cssText = `display:grid;gap:14px;${this.#narrow ? 'grid-template-columns:1fr' : 'grid-template-columns:repeat(3,1fr)'}`;
    const ICON = { 50: '🎴', 250: '🎁', 2500: '💎' };
    for (const tier of GACHA_TIERS) {
      const price = gachaGoldPrice(tier);
      const affordable = this.#gold >= price;
      const card = document.createElement('div');
      card.style.cssText = [
        'display:flex',
        'flex-direction:column',
        'gap:6px',
        'padding:16px',
        'border-radius:14px',
        'background:rgba(8,18,12,0.85)',
        `border:1px solid ${affordable ? 'rgba(52,211,153,0.55)' : 'rgba(120,120,120,0.4)'}`,
        'align-items:center',
        'text-align:center',
      ].join(';');
      card.innerHTML =
        `<div style="font-size:40px">${ICON[tier] || '🎴'}</div>` +
        `<div style="color:#eafff2;font:700 19px system-ui">€${tier} Pack</div>` +
        `<div style="color:#9fc4ad;font:500 12px system-ui">${this.#esc(this.#category)} · mystery pull</div>` +
        `<div style="color:#f5b23e;font:700 18px system-ui">${price.toLocaleString()} Gold</div>`;
      const btn = document.createElement('button');
      btn.textContent = affordable ? 'ORDER PACK' : 'Not enough Gold';
      btn.disabled = !affordable;
      btn.style.cssText = [
        'margin-top:6px',
        'padding:10px 14px',
        'border-radius:10px',
        `cursor:${affordable ? 'pointer' : 'not-allowed'}`,
        'font:700 14px system-ui',
        'width:100%',
        `border:1px solid ${affordable ? '#34d399' : 'rgba(120,120,120,0.4)'}`,
        `background:${affordable ? '#34d399' : 'rgba(40,40,40,0.6)'}`,
        `color:${affordable ? '#03210f' : '#888'}`,
      ].join(';');
      if (affordable) btn.onclick = () => this.#openGachaConfirm(tier);
      card.appendChild(btn);
      packs.appendChild(card);
    }
    wrap.appendChild(packs);
    document.body.appendChild(wrap);
    this.#domEls.push(wrap);
  }

  #openGachaConfirm(tier, payWith = 'gold') {
    try {
      this.#openGachaConfirmInner(tier, payWith);
    } catch (err) {
      console.error('[card-shop] gacha confirm failed:', err);
      this.#renderGacha();
    }
  }

  /** Confirm + shipping modal for a gacha pack order (IRL, team-fulfilled). Pay in Gold, $MONSTARA or
   *  SOL — the crypto rails verify an inbound transfer (no Gold debit). */
  #openGachaConfirmInner(tier, payWith = 'gold') {
    this.#destroyDom();
    const wallet = netManager.wallet;
    const crypto = payWith === 'token' || payWith === 'sol';
    const overlay = this.#modalOverlay();
    const panel = this.#modalPanel(overlay, 520);
    const back = () => (crypto ? this.#backToTopup(overlay) : this.#backToGacha(overlay));
    panel.appendChild(this.#h2(`€${tier} Gacha Pack`));

    let unit = '';
    if (crypto) {
      const q = (this.#topupCfg?.gacha || []).find((g) => g.tier === tier);
      if (!q) {
        panel.appendChild(this.#note('Price unavailable — reopen Top-Up and try again.', '#ff9a9a'));
        panel.appendChild(this.#modalButtons(overlay, [{ label: 'Back', onClick: back }]));
        return;
      }
      unit = payWith === 'token' ? '$MONSTARA' : 'SOL';
      const amt = payWith === 'token' ? q.token : q.sol;
      panel.appendChild(
        this.#note(`${this.#category} · mystery pull · ${amt.toLocaleString()} ${unit} (≈ $${q.usd})`, '#aef0c4')
      );
    } else {
      const price = gachaGoldPrice(tier);
      const affordable = this.#gold >= price;
      panel.appendChild(
        this.#note(
          `${this.#category} · mystery pull · ${price.toLocaleString()} Gold (balance ${this.#gold.toLocaleString()})`,
          affordable ? '#aef0c4' : '#ff9a9a'
        )
      );
      if (!affordable) {
        panel.appendChild(this.#note(`You need ${(price - this.#gold).toLocaleString()} more Gold — win battles, or top up instantly.`, '#ff9a9a'));
        panel.appendChild(
          this.#modalButtons(overlay, [
            { label: 'Back', onClick: back },
            { label: 'Top up Gold →', primary: true, onClick: () => { this.#closeOverlay(overlay); this.#switchView('topup'); } },
          ])
        );
        return;
      }
    }
    if (!wallet) {
      panel.appendChild(this.#note('Connect your wallet to order.', '#ff9a9a'));
      panel.appendChild(this.#modalButtons(overlay, [{ label: 'Close', onClick: back }]));
      return;
    }
    if (crypto && !canPayToken()) {
      panel.appendChild(this.#note('Connect your wallet so it can sign the crypto payment.', '#ffd9a0'));
      const status = this.#note('', '#9fc4ad');
      panel.appendChild(status);
      panel.appendChild(this.#modalButtons(overlay, [
        {
          label: 'Connect wallet',
          primary: true,
          onClick: async () => {
            status.textContent = 'Opening your wallet…';
            const ok = await connectPayProvider();
            if (ok) this.#openGachaConfirmInner(tier, payWith);
            else status.textContent = 'No sign-capable wallet found — open/install Phantom, then try again.';
          },
        },
        { label: 'Back', onClick: back },
      ]));
      return;
    }

    panel.appendChild(this.#h3('Shipping details'));
    const fields = {
      name: this.#field('Full name'),
      address: this.#field('Street address'),
      city: this.#field('City'),
      postal: this.#field('Postal / ZIP code'),
      country: this.#field('Country'),
    };
    Object.values(fields).forEach((f) => panel.appendChild(f.wrap));
    const twitterField = this.#field('Twitter / X handle (so the team can reach you)');
    panel.appendChild(twitterField.wrap);
    const errLine = this.#note('', '#ff9a9a');
    panel.appendChild(errLine);
    let busy = false;
    const submit = {
      label: crypto ? `Pay ${unit}` : `Order €${tier} pack`,
      primary: true,
      onClick: async () => {
        if (busy) return;
        const twitter = twitterField.input.value.trim();
        if (!twitter) {
          errLine.style.color = '#ff9a9a';
          errLine.textContent = 'Please add your Twitter / X handle so the team can reach you.';
          return;
        }
        const shipping = {
          name: fields.name.input.value.trim(),
          address: fields.address.input.value.trim(),
          city: fields.city.input.value.trim(),
          postal: fields.postal.input.value.trim(),
          country: fields.country.input.value.trim(),
        };
        if (Object.values(shipping).some((v) => !v)) {
          errLine.style.color = '#ff9a9a';
          errLine.textContent = 'All shipping fields are required.';
          return;
        }
        busy = true;
        let result;
        if (crypto) {
          errLine.style.color = '#9fc4ad';
          errLine.textContent = 'Refreshing price…';
          const cfg = await this.#ensureTopupConfig(true);
          const q = (cfg.gacha || []).find((g) => g.tier === tier);
          const amt = payWith === 'token' ? q?.token : q?.sol;
          if (!cfg.enabled || !amt) {
            busy = false;
            errLine.style.color = '#ff9a9a';
            errLine.textContent = 'Price unavailable — please try again.';
            return;
          }
          errLine.textContent = 'Approve the payment in your wallet…';
          const rpc = import.meta.env.VITE_RPC_URL || '';
          const sig =
            payWith === 'token'
              ? await payTokenToTreasury(cfg, amt, rpc)
              : await paySolToTreasury(cfg.treasury, amt, rpc);
          if (!sig) {
            busy = false;
            errLine.style.color = '#ff9a9a';
            errLine.textContent = 'Payment was not completed. Please try again.';
            return;
          }
          errLine.textContent = 'Verifying payment…';
          result = await placeGachaOrder({ wallet, tier, category: this.#category, shipping, twitter, payWith, paymentSig: sig });
        } else {
          errLine.style.color = '#9fc4ad';
          errLine.textContent = 'Placing order…';
          result = await placeGachaOrder({ wallet, tier, category: this.#category, shipping, twitter });
        }
        busy = false;
        if (result.ok === true) {
          if (!crypto) await this.#refreshGold();
          this.#showGachaSuccess(overlay, tier);
          return;
        }
        errLine.style.color = '#ff9a9a';
        errLine.textContent = crypto
          ? 'Payment sent but the order could not be recorded — keep your transaction and contact the team.'
          : result.ok === false && result.reason === 'insufficient'
            ? 'Not enough Gold — your balance changed. Win more battles!'
            : 'Could not place the order. Please try again.';
      },
    };
    panel.appendChild(this.#modalButtons(overlay, [{ label: 'Cancel', onClick: back }, submit]));
  }

  #showGachaSuccess(overlay, tier) {
    overlay.innerHTML = '';
    const panel = this.#modalPanel(overlay, 440);
    const check = document.createElement('div');
    check.textContent = '✓';
    check.style.cssText =
      'width:64px;height:64px;border-radius:50%;background:rgba(52,211,153,0.15);border:2px solid #34d399;color:#34d399;font:700 34px system-ui;display:flex;align-items:center;justify-content:center;margin:0 auto 14px;';
    panel.appendChild(check);
    panel.appendChild(this.#h2('Gacha ordered! 🎁'));
    panel.appendChild(
      this.#note(
        `Your €${tier} ${this.#category} gacha pack is in! The team will open it on CollectorCrypt, DM you on Twitter / X, and ship the pull. Track it under "My Orders".`,
        '#aef0c4'
      )
    );
    panel.appendChild(
      this.#modalButtons(overlay, [
        { label: 'View My Orders', primary: true, onClick: () => { this.#closeOverlay(overlay); this.#switchView('orders'); } },
        { label: 'Done', onClick: () => this.#backToGacha(overlay) },
      ])
    );
  }

  #backToGacha(overlay) {
    this.#closeOverlay(overlay);
    this.#renderGacha();
  }

  #backToGrid(overlay) {
    this.#closeOverlay(overlay);
    if (this.#view === 'grid') this.#renderGrid();
  }

  // ── MY ORDERS ──

  async #loadOrders() {
    this.#destroyDom();
    this.#gridContainer?.removeAll(true);

    const wallet = netManager.wallet;
    if (!apiConfigured()) {
      this.#showStatus('Orders are offline — connect to the server to view your redemptions.');
      return;
    }
    if (!wallet) {
      this.#showStatus('Connect your wallet to view your orders.');
      return;
    }
    this.#showStatus('Loading your orders…');
    const orders = await fetchShopOrders(wallet);
    if (this.#dead() || this.#view !== 'orders') return;

    if (orders.length === 0) {
      this.#showStatus('No orders yet. Redeem a card to see it here.');
      return;
    }
    this.#hideStatus();
    this.#renderOrders(orders);
  }

  /** @param {import('../net/api.js').ShopOrder[]} orders */
  #renderOrders(orders) {
    const rect = this.#panelDomRect(40);
    const scroll = document.createElement('div');
    scroll.style.cssText = [
      'position:fixed',
      `left:${rect.left}px`,
      `top:${rect.top}px`,
      `width:${rect.width}px`,
      `height:${rect.height}px`,
      'overflow-y:auto',
      'display:flex',
      'flex-direction:column',
      'gap:12px',
      'padding:8px 14px 8px 8px',
      'box-sizing:border-box',
      'z-index:60',
      'scrollbar-width:thin',
      'scrollbar-color:#34d399 transparent',
    ].join(';');
    document.body.appendChild(scroll);
    this.#scrollEl = scroll;
    this.#domEls.push(scroll);

    for (const o of orders) scroll.appendChild(this.#orderEl(o));
  }

  /** @param {import('../net/api.js').ShopOrder} o */
  #orderEl(o) {
    const el = document.createElement('div');
    el.style.cssText =
      'display:flex;gap:14px;align-items:center;padding:12px;border-radius:12px;background:rgba(8,18,12,0.92);border:1px solid rgba(52,211,153,0.35);';
    const img = document.createElement('img');
    img.src = o.cardImage ?? '';
    img.referrerPolicy = 'no-referrer';
    img.style.cssText = 'width:60px;height:80px;object-fit:cover;border-radius:8px;background:#0a1410;';
    img.onerror = () => (img.style.display = 'none');

    const mid = document.createElement('div');
    mid.style.cssText = 'flex:1;display:flex;flex-direction:column;gap:3px;min-width:0;';
    const date = o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '';
    mid.innerHTML =
      `<div style="color:#eafff2;font:600 14px system-ui;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${this.#esc(o.cardName ?? 'Card')}</div>` +
      `<div style="color:#f5b23e;font:600 13px system-ui">${(o.priceGold ?? 0).toLocaleString()} Gold spent</div>` +
      (date ? `<div style="color:#7d9c8a;font:500 11px system-ui">${date}</div>` : '');

    const status = this.#statusBadge(o.status, o.tracking);
    el.append(img, mid, status);
    return el;
  }

  /** @param {import('../net/api.js').ShopOrderStatus} status @param {string|undefined} tracking */
  #statusBadge(status, tracking) {
    const map = {
      pending_review: { label: 'Pending review', color: '#f5b23e', bg: 'rgba(245,178,62,0.12)' },
      approved: { label: 'Approved', color: '#7af0c0', bg: 'rgba(122,240,192,0.12)' },
      shipped: { label: 'Shipped', color: '#34d399', bg: 'rgba(52,211,153,0.14)' },
      rejected: { label: 'Rejected', color: '#ff7a7a', bg: 'rgba(255,122,122,0.12)' },
    };
    const s = map[status] ?? { label: status, color: '#9fc4ad', bg: 'rgba(159,196,173,0.12)' };
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;flex-direction:column;align-items:flex-end;gap:4px;flex-shrink:0;';
    const badge = document.createElement('div');
    badge.textContent = s.label;
    badge.style.cssText = `padding:5px 12px;border-radius:999px;background:${s.bg};color:${s.color};border:1px solid ${s.color};font:600 12px system-ui;white-space:nowrap;`;
    wrap.appendChild(badge);
    if (status === 'shipped' && tracking) {
      const trk = document.createElement('div');
      trk.textContent = `# ${tracking}`;
      trk.style.cssText = 'color:#9fc4ad;font:500 11px system-ui;';
      wrap.appendChild(trk);
    }
    return wrap;
  }

  // ── helpers: status text + DOM modal primitives ──

  #showStatus(msg) {
    this.#destroyDom();
    this.#gridContainer?.removeAll(true);
    this.#statusText?.setText(msg).setVisible(true);
  }
  #hideStatus() {
    this.#statusText?.setVisible(false);
  }

  /** Compute a DOM rect (px) for the panel interior, inset by `pad`, offset below the top bar. */
  #panelDomRect(pad) {
    const canvas = this.game.canvas;
    const bounds = canvas.getBoundingClientRect();
    const sx = bounds.width / this.scale.width;
    const sy = bounds.height / this.scale.height;
    const topInset = 12; // tabs/filters now live ABOVE the panel, so only a small inner inset
    const left = bounds.left + (this.#panelX - this.#panelW / 2 + pad) * sx;
    const top = bounds.top + (this.#panelY - this.#panelH / 2 + topInset) * sy;
    const width = (this.#panelW - pad * 2) * sx;
    const height = (this.#panelH - topInset - pad) * sy;
    return { left, top, width, height };
  }

  #modalOverlay() {
    const o = document.createElement('div');
    o.style.cssText =
      'position:fixed;inset:0;background:rgba(3,12,7,0.82);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:120;';
    document.body.appendChild(o);
    this.#domEls.push(o);
    return o;
  }
  #modalPanel(overlay, maxWidth) {
    const p = document.createElement('div');
    p.style.cssText = [
      `width:min(${maxWidth}px,92vw)`,
      'max-height:88vh',
      'overflow-y:auto',
      'box-sizing:border-box',
      'padding:24px',
      'border-radius:18px',
      'background:rgba(10,20,14,0.97)',
      'border:1px solid rgba(52,211,153,0.5)',
      'box-shadow:0 0 40px rgba(52,211,153,0.18)',
    ].join(';');
    overlay.appendChild(p);
    return p;
  }
  #closeOverlay(overlay) {
    overlay.remove();
    this.#domEls = this.#domEls.filter((e) => e !== overlay);
  }
  #h2(t) {
    const e = document.createElement('div');
    e.textContent = t;
    e.style.cssText = 'color:#34d399;font:700 24px system-ui;text-align:center;margin-bottom:14px;';
    return e;
  }
  #h3(t) {
    const e = document.createElement('div');
    e.textContent = t;
    e.style.cssText = 'color:#aef0c4;font:600 15px system-ui;margin:6px 0 8px;';
    return e;
  }
  #note(t, color) {
    const e = document.createElement('div');
    e.textContent = t;
    e.style.cssText = `color:${color};font:500 13px system-ui;text-align:center;margin:8px 0;min-height:18px;`;
    return e;
  }
  /** A selectable "pay with" option card (Gold | $TOKEN). `on` sets the initial highlighted state. */
  #payOption(title, sub, on) {
    const el = document.createElement('button');
    el.type = 'button';
    el.dataset.on = on ? '1' : '';
    el.style.cssText = [
      'flex:1', 'text-align:left', 'cursor:pointer', 'box-sizing:border-box',
      'padding:10px 12px', 'border-radius:12px',
      on ? 'background:rgba(245,178,62,0.10)' : 'background:rgba(8,18,12,0.85)',
      `border:1px solid ${on ? '#f5b23e' : 'rgba(52,211,153,0.4)'}`,
      'transition:border-color .15s,background .15s',
    ].join(';');
    el.innerHTML =
      `<div style="color:#eafff2;font:600 14px system-ui;margin-bottom:2px">${this.#esc(title)}</div>` +
      `<div style="color:#9fc4ad;font:500 12px system-ui">${this.#esc(sub)}</div>`;
    return el;
  }
  #field(placeholder) {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'margin-bottom:10px;';
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = placeholder;
    input.style.cssText = [
      'width:100%',
      'box-sizing:border-box',
      'padding:11px 14px',
      'border-radius:12px',
      'border:1px solid rgba(52,211,153,0.4)',
      'background:rgba(8,18,12,0.85)',
      'color:#eafff2',
      'outline:none',
      'font:500 15px system-ui',
    ].join(';');
    input.onfocus = () => (input.style.borderColor = '#f5b23e');
    input.onblur = () => (input.style.borderColor = 'rgba(52,211,153,0.4)');
    wrap.appendChild(input);
    return { wrap, input };
  }
  /** @param {HTMLElement} overlay @param {{label:string,primary?:boolean,onClick:()=>void}[]} buttons */
  #modalButtons(overlay, buttons) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:12px;margin-top:14px;justify-content:center;flex-wrap:wrap;';
    for (const b of buttons) {
      const btn = document.createElement('button');
      btn.textContent = b.label;
      btn.style.cssText = b.primary
        ? 'flex:1;min-width:140px;padding:12px 18px;border-radius:12px;border:none;background:#18b85f;color:#03210f;font:700 15px system-ui;cursor:pointer;'
        : 'padding:12px 18px;border-radius:12px;border:1px solid rgba(159,196,173,0.4);background:transparent;color:#9fc4ad;font:600 15px system-ui;cursor:pointer;';
      btn.onmouseenter = () => {
        if (b.primary) btn.style.background = '#22d46f';
        else btn.style.borderColor = '#f5b23e';
      };
      btn.onmouseleave = () => {
        if (b.primary) btn.style.background = '#18b85f';
        else btn.style.borderColor = 'rgba(159,196,173,0.4)';
      };
      btn.onclick = () => b.onClick();
      row.appendChild(btn);
    }
    return row;
  }
  #esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  #destroyDom() {
    this.#domEls.forEach((e) => e.remove());
    this.#domEls = [];
    this.#scrollEl = undefined;
  }

  // ── lifecycle ──

  update() {
    super.update();
    if (this._controls?.wasBackKeyPressed?.()) {
      // ESC closes any open modal first, else the scene.
      const overlay = this.#domEls.find((e) => e.style.zIndex === '120');
      if (overlay) {
        this.#backToGrid(overlay);
        return;
      }
      this.#close();
    }
  }

  #close() {
    // Guard re-entry: a rapid double-close / shutdown race could otherwise queue two teardowns
    // (or fire the fallback after the fade already completed) and leave input locked.
    if (this.#closing) return;
    this.#closing = true;

    this.#destroyDom();
    this.cameras.main.fadeOut(200, 0, 0, 0);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => this.#finishClose());
    // Fallback: if the fade is preempted (scene stopped mid-fade), FADE_OUT_COMPLETE never fires,
    // so the scene would never stop and the world would stay frozen. Force teardown after the
    // fade's nominal duration. #finishClose is idempotent, so whichever path wins is safe.
    this.time.delayedCall(260, () => this.#finishClose());
  }

  /**
   * Idempotent teardown: stop this overlay and resume/wake the previous scene, always releasing
   * its input. Safe to call multiple times (fade-complete + delayedCall fallback both call it).
   * @returns {void}
   */
  /** True once the shop is closing or no longer the active scene — every async fetch/render MUST bail
   *  on this before adding any DOM/Phaser, or a request resolving during the close fade re-appends the
   *  shop over the world ("can't close it") or touches a torn-down scene (the reported JS errors). */
  #dead() {
    return this.#closing || !this.sys.isActive();
  }

  #finishClose() {
    if (!this.scene.isActive(SCENE_KEYS.CARD_SHOP_SCENE)) return;
    // Nuke ALL shop DOM up-front so nothing can linger over the world during/after teardown.
    this.#destroyDom();
    this.#toolbarEl?.remove();
    this.#toolbarEl = undefined;
    this.#closeEl?.remove();
    this.#closeEl = undefined;
    const prev = this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE;
    this.scene.stop(SCENE_KEYS.CARD_SHOP_SCENE);
    if (this.scene.isPaused(prev)) {
      this.scene.resume(prev);
    } else if (this.scene.isSleeping(prev)) {
      this.scene.wake(prev);
      this.scene.bringToTop(prev);
    } else if (!this.scene.isActive(prev)) {
      this.scene.start(prev);
    }
    const prevScene = this.scene.get(prev);
    if (prevScene?._controls) prevScene._controls.lockInput = false;
  }
}
